import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import _get from 'lodash/get'
import { List, Map } from 'immutable'
import { useQuery } from '@apollo/client'
import { getBoundActions } from '../../actions/index'
import {
  connect as connectPage,
  PAGE_SEO_TYPE_LOCATION,
} from '../Page/connect'
import { getPrimary } from '../../services/languages/index'
import { GET_PLAYLISTS, GET_PURCHASES_LIST } from '../../services/playlist/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../services/apollo-graphql/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { historyRedirect } from '../../services/navigation/index'
import { TYPE_USER_PLAYLIST_ADD_REMOVE, TYPE_LOGIN } from '../../services/dialog/index'
import {
  URL_PLAYLIST,
  URL_PLAYLIST_WATCH_LATER,
  URL_PLAYLIST_PURCHASES_VIDEOS,
  URL_PLAYLIST_PURCHASES_EBOOKS,
  URL_PLAYLIST_BONUS_VIDEOS,
  URL_PLAYLIST_BONUS_EBOOKS,
} from '../../services/url/constants'
import Sherpa, { TYPE_SMALL_BLUE } from '../Sherpa/index'
import {
  CONTENT_IMAGE_LIST,
} from '../../services/content/index'
import {
  getPageStateByPath,
  getUserPlaylists,
  getUserPurchases,
  pageViews,
} from './utils'
import MyLibraryNav from './MyLibraryNav'
import MyLibraryWatchHistory from './MyLibraryWatchHistory'
import MyLibraryPlaylist from './MyLibraryPlaylist'
import MyLibraryPurchasesVideos from './MyLibraryPurchasesVideos'
import MyLibraryPurchasesEBooks from './MyLibraryPurchasesEBooks'

const MyLibraryPage = (props) => {
  const {
    auth,
    user,
    history,
    location,
    staticText,
    renderModal,
    setOverlayDialogVisible,
  } = props

  const isLoggedIn = getAuthIsLoggedIn(auth)
  const language = getPrimary(user.getIn(['data', 'language'], List()))

  const [pageState, setPageState] = useState(null)
  const [defaultPlaylists, setDefaultPlaylists] = useState(List())
  const [customPlaylists, setCustomPlaylists] = useState(List())
  const [purchasesCount, setPurchasesCount] = useState(0)
  const [purchasesCountTotal, setPurchasesCountTotal] = useState(0)
  const [userPurchases, setUserPurchases] = useState({
    contentList: List(),
    assetsList: List(),
  })
  const [userBonuses, setUserBonuses] = useState({
    contentList: List(),
    assetsList: List(),
  })

  const {
    data: playlistsData,
    loading: playlistsLoading,
    refetch: refetchPlaylists,
  } = useQuery(GET_PLAYLISTS, {
    skip: !isLoggedIn,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: auth.get('jwt'),
    },
  })

  const {
    data: purchasesData,
    loading: purchasesLoading,
    fetchMore: fetchMorePurchases,
  } = useQuery(GET_PURCHASES_LIST, {
    skip: !isLoggedIn,
    variables: {
      language: [language],
      imageType: CONTENT_IMAGE_LIST,
      imageLanguage: [language, '_primary'],
      assetLanguage: language,
      learnerLevelLanguage: language,
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: auth.get('jwt'),
    },
  })

  const redirectToUrl = (url) => {
    historyRedirect({
      url,
      language,
      history,
      auth,
    })
  }

  const handleCreateClick = () => {
    renderModal(TYPE_USER_PLAYLIST_ADD_REMOVE, {
      onReturnFocus: () => {},
      createOnly: true,
      uiLocation: 'My Library',
      uiElement: 'New Playlist Button',
      onCreate: async (playlist) => {
        global.scrollTo(0, 0)
        await refetchPlaylists()
        historyRedirect({
          url: `${URL_PLAYLIST}/${playlist.get('playlistType')}`,
          language,
          history,
          auth,
        })
      },
    })
  }

  useEffect(() => {
    if (playlistsData && purchasesData && !playlistsLoading && !purchasesLoading) {
      const playlistsList = _get(playlistsData, ['playlistList', 'playlists'], []) || []
      const userPlaylists = getUserPlaylists(playlistsList, staticText)
      const purchasesList = _get(purchasesData, ['ecommerceSkuMappingsList', 'ecommerceSkuMappings'], {}) || {}
      const countTotal = _get(purchasesData, ['ecommerceSkuMappingsList', 'count'], 0)
      const formattedPurchases = getUserPurchases(purchasesList)
      const newState = getPageStateByPath(
        location.pathname,
        [...userPlaylists.defaultPlaylists, ...userPlaylists.customPlaylists],
        formattedPurchases,
        staticText,
      )
      setUserPurchases(formattedPurchases.purchases)
      setUserBonuses(formattedPurchases.bonus)
      setDefaultPlaylists(userPlaylists.defaultPlaylists)
      setCustomPlaylists(userPlaylists.customPlaylists)
      setPurchasesCountTotal(countTotal)
      setPurchasesCount(purchasesCount)

      // Has purchases or bonus  content
      const hasPurchasesVideos = userPurchases.contentList.size > 0
      const hasBonusVideos = userBonuses.contentList.size > 0

      // Redirect to next available page
      if (newState) {
        setPageState(newState)
      } else if (location.pathname === URL_PLAYLIST_PURCHASES_VIDEOS && !hasPurchasesVideos) {
        redirectToUrl(URL_PLAYLIST_PURCHASES_EBOOKS)
      } else if (location.pathname === URL_PLAYLIST_BONUS_VIDEOS && !hasBonusVideos) {
        redirectToUrl(URL_PLAYLIST_BONUS_EBOOKS)
      } else if (location.pathname !== URL_PLAYLIST_WATCH_LATER) {
        // When not logged in on purchases-videos/ebooks, stay so login modal remains visible
        const isPurchasesPath = location.pathname === URL_PLAYLIST_PURCHASES_VIDEOS
          || location.pathname === URL_PLAYLIST_PURCHASES_EBOOKS
        if (isLoggedIn || !isPurchasesPath) {
          redirectToUrl(URL_PLAYLIST_WATCH_LATER)
        }
      }
    }
  }, [
    location.pathname,
    playlistsData,
    playlistsLoading,
    purchasesData,
    purchasesLoading,
    staticText,
  ])

  useEffect(() => {
    if (!isLoggedIn) {
      setOverlayDialogVisible(TYPE_LOGIN)
    }
  }, [isLoggedIn])

  if (!isLoggedIn || !pageState || playlistsLoading || purchasesLoading) {
    return (
      <div className="my-library__page">
        <div className="my-library__page-loading">
          <Sherpa className={['my-library__page-sherpa']} type={TYPE_SMALL_BLUE} />
        </div>
      </div>
    )
  }

  return (
    <div className="my-library__page">
      <div className="my-library__nav-container">
        <MyLibraryNav
          onCreateClick={handleCreateClick}
          location={location}
          staticText={staticText}
          defaultPlaylists={defaultPlaylists}
          customPlaylists={customPlaylists}
          userPurchases={userPurchases}
          userBonuses={userBonuses}
          title={pageState.name}
        />
      </div>
      <div className="my-library__content-container">
        {pageState
          ? <>
            {pageState.view === pageViews.WATCH_HISTORY
              && <MyLibraryWatchHistory />}

            {pageState.view === pageViews.PLAYLIST
              && <MyLibraryPlaylist pageState={pageState} onPlaylistRename={refetchPlaylists}/>}

            {pageState.view === pageViews.BONUS_VIDEOS
              && <MyLibraryPurchasesVideos
              pageState={pageState}
              contentList={userBonuses.contentList}
              fetchMore={fetchMorePurchases}
              loading={purchasesLoading}
              count={purchasesCount}
              totalCount={purchasesCountTotal}
            />}

            {pageState.view === pageViews.BONUS_EBOOKS
              && <MyLibraryPurchasesEBooks
              pageState={pageState}
              assetsList={userBonuses.assetsList}
              fetchMore={fetchMorePurchases}
              loading={purchasesLoading}
              count={purchasesCount}
              totalCount={purchasesCountTotal}
            />}

            {pageState.view === pageViews.PURCHASES_VIDEOS
              && <MyLibraryPurchasesVideos
              pageState={pageState}
              contentList={userPurchases.contentList}
              fetchMore={fetchMorePurchases}
              loading={purchasesLoading}
              count={purchasesCount}
              totalCount={purchasesCountTotal}
            />}

            {pageState.view === pageViews.PURCHASES_EBOOKS
              && <MyLibraryPurchasesEBooks
              pageState={pageState}
              assetsList={userPurchases.assetsList}
              fetchMore={fetchMorePurchases}
              loading={purchasesLoading}
              count={purchasesCount}
              totalCount={purchasesCountTotal}
            />}
          </>
          : null
        }
      </div>
    </div>
  )
}

MyLibraryPage.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  setOverlayDialogVisible: PropTypes.func.isRequired,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connect(
    (state) => ({
      auth: state.auth,
      user: state.user,
      staticText: state.staticText.getIn(['data', 'myLibraryPage', 'data'], Map()) || Map(),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        addToasty: actions.toasty.addToasty,
        dismissModal: actions.dialog.dismissModal,
        renderModal: actions.dialog.renderModal,
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
      }
    },
  ),
)(MyLibraryPage)
